import React, { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import "./larus.css";
import "./larus-extra.css";
import "./larus-v6.css";
import "./larus-brand.css";
import "./larus-gull.css";
import "./larus-system.css";
import "./larus-alive.css";
import "./larus-e2.css";
import "./larus-flex.css";
import "./larus-e4.css";
import "./larus-e6.css";
import "./larus-e9.css";
import { AuthProvider, useAuth } from "./auth";
import { Login, Forgot, Reset, AcceptInvite } from "./screens/Auth";
import { LibraryPage, Integrations, Operations, Access } from "./screens/Workspace";
import ExplorerPage from "./screens/Explorer";
import Portfolio from "./screens/Portfolio";
import Overview2 from "./screens/Overview2";
import SharedView from "./screens/SharedView";
import Connections from "./screens/Connections";
import { SCREENS } from "./screens/registry";
import { bp, progUrl, loadSlugs, resolvePid, slugKnown, slugsLoaded, useSlugs } from "./slug";

const CANON = process.env.REACT_APP_CANONICAL_URL;
// the technical *.emergent.host address forwards to larus.canus.io once that domain answers
if (CANON && window.location.hostname.endsWith(".emergent.host")) {
  const ctl = new AbortController();
  setTimeout(() => ctl.abort(), 4000);
  fetch(`${CANON}/api/health`, { mode: "no-cors", cache: "no-store", signal: ctl.signal })
    .then(() => window.location.replace(CANON + window.location.pathname + window.location.search + window.location.hash))
    .catch(() => {});
}

function Guard({ children }: { children: React.ReactElement }) {
  const { user } = useAuth();
  useEffect(() => { if (user && !slugsLoaded()) loadSlugs(); }, [user]);
  if (user === null) return <div className="emptyline" data-testid="session-checking">Checking session…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

const OLD: Record<string, string> = { desk: "build", mine: "build", participants: "people" };
function ProgrammeScreen() {
  useSlugs();
  const { screen = "", pid: raw } = useParams();
  const loc = useLocation();
  const [tried, setTried] = useState(false);
  const known = slugKnown(raw);
  useEffect(() => { if (!known && !tried) loadSlugs().finally(() => setTried(true)); }, [known, tried]);
  if (!known && !tried) return <div className="emptyline" data-testid="session-checking">Opening…</div>;
  const pretty = bp(resolvePid(raw));
  if (OLD[screen]) return <Navigate to={`${pretty}/${OLD[screen]}${loc.search}`} replace />;
  if (`/${raw}` !== pretty) return <Navigate to={`${pretty}/${screen}${loc.search}${loc.hash}`} replace />;
  const C = SCREENS[screen];
  return C ? <C /> : <Navigate to={`${pretty}/build`} replace />;
}

/* Old /p/<id>/<screen> and /prog/<id> links (emails, bookmarks, server controls) land on the readable address. */
function LegacyBuild() {
  useSlugs();
  const { pid, screen } = useParams();
  const loc = useLocation();
  return <Navigate to={`${bp(pid)}/${screen}${loc.search}${loc.hash}`} replace />;
}
function LegacyProgramme() {
  useSlugs();
  const { rid } = useParams();
  return <Navigate to={progUrl(rid)} replace />;
}

export default function LarusApp() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<Forgot />} />
          <Route path="/reset-password" element={<Reset />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />
          <Route path="/" element={<Guard><ExplorerPage /></Guard>} />
          <Route path="/o/:oid" element={<Guard><ExplorerPage /></Guard>} />
          <Route path="/programme/:rid" element={<Guard><ExplorerPage /></Guard>} />
          <Route path="/prog/:rid" element={<Guard><LegacyProgramme /></Guard>} />
          <Route path="/portfolio" element={<Guard><Portfolio /></Guard>} />
          <Route path="/overview" element={<Guard><Overview2 /></Guard>} />
          <Route path="/view/:token" element={<SharedView />} />
          <Route path="/programmes" element={<Navigate to="/" replace />} />
          <Route path="/work" element={<Navigate to="/" replace />} />
          <Route path="/library" element={<Guard><LibraryPage /></Guard>} />
          <Route path="/integrations" element={<Guard><Integrations /></Guard>} />
          <Route path="/connections" element={<Guard><Connections /></Guard>} />
          <Route path="/operations" element={<Guard><Operations /></Guard>} />
          <Route path="/settings" element={<Guard><Access /></Guard>} />
          <Route path="/p/:pid/:screen" element={<Guard><LegacyBuild /></Guard>} />
          <Route path="/:pid/:screen" element={<Guard><ProgrammeScreen /></Guard>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AuthProvider>
  );
}
